import { DraggableAttributes } from '@dnd-kit/core';
import { SyntheticListenerMap } from '@dnd-kit/core/dist/hooks/utilities';
import { useSortable } from '@dnd-kit/sortable';
import { CSS as DndCSS } from '@dnd-kit/utilities';
import React, { CSSProperties, createContext, useContext, useMemo } from 'react';

const SortableItemContext = createContext<SortableItemContextResult>({
  style: {},
  isActive: false,
  setNodeRef: () => {
    return;
  },
  elementProps: {
    attributes: undefined,
    listeners: undefined,
  },
});

type SortableItemContextProps = {
  id: string;
  children: React.ReactNode;
};

export const SortableItemContextProvider = ({ id, children }: SortableItemContextProps) => {
  const { setNodeRef, attributes, listeners, transform, transition, activeIndex, index } =
    useSortable({ id });

  const style = {
    transform: DndCSS.Transform.toString(transform),
    transition,
  };

  const isActive = index >= 0 && activeIndex === index;

  const elementProps = useMemo(
    () => ({
      attributes,
      listeners,
    }),
    [attributes, listeners],
  );

  const value: SortableItemContextResult = {
    style,
    isActive,
    setNodeRef,
    elementProps,
  };

  return <SortableItemContext.Provider value={value}>{children}</SortableItemContext.Provider>;
};

interface SortableItemContextResult {
  style: CSSProperties;
  isActive: boolean;
  setNodeRef: (node: HTMLElement) => void;
  elementProps: {
    attributes?: DraggableAttributes;
    listeners?: SyntheticListenerMap;
  };
}

export const useSortableItemContext = () =>
  useContext<SortableItemContextResult>(SortableItemContext);
